// ── Collateral cards shared by the Borrower Dashboard and the certificate page ──
// The A/R, A/P and Inventory tabs on the two pages are meant to be the same.
// Cards that live here are rendered by both, so they cannot drift apart.
//
// Each card takes the account's history (newest first, getBorrowingBases order),
// the id of the certificate being looked at (highlighted in its table — the
// latest one on the dashboard, the open one on the certificate page), and
// onOpen(bbid) for clicking a row.
import { useState, useRef, useLayoutEffect, useMemo } from "react";
import { B } from "../../shared/theme";
import { fmt, fmtDate, fmtRatio } from "../../shared/format";
import { Card, Td, TdNum } from "../../shared/ui";
import { inventoryInsights } from "../../shared/inventoryInsights";
import { ArExcessTrend, PairTrend, BarTrend, StackTrend, ScrollTrend, stY, monthLbl, TH_WRAP } from "./trendCharts";
import { arMetricsByRow } from "./arMetrics";

const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, paddingBottom: 6, borderBottom: "1.5px solid " + B.cyanLight };
const row = { display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 };
const thSticky = { fontSize: 12, fontWeight: 600, color: B.cyanDark, background: "#ffffff", ...TH_WRAP, position: "sticky", top: 0, zIndex: 3, borderBottom: "2px solid " + B.cyan, boxShadow: "0 2px 4px rgba(0,0,0,0.06)" };
const count = n => <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>({n})</span>;

// A/R Trend — nested subsets of one measure → sequential cyan ramp (light → dark).
const TSER = [
  { id: "end", label: "Ending A/R", key: "ar_ending_balance", color: "#A5E3F1", w: 0.72 },
  { id: "elig", label: "Eligible A/R", key: "ar_total_eligible", color: "#12b5db", w: 0.48 },
  { id: "net", label: "Net Avail A/R", key: "ar_net_available", color: "#116588", w: 0.26 },
];
// A/R Rollforward — Beginning A/R vs Total Ending A/R (activity pieces in the table).
const ARSER = [
  { key: "ar_beginning_balance", label: "Beginning A/R", color: "#A5E3F1", w: 0.6 },
  { key: "ar_ending_balance", label: "Total Ending A/R", color: "#116588", w: 0.34 },
];
const INVSER = [
  { key: "inv_gross",          label: "Gross (at cost)", color: "#A5E3F1", w: 1.00 },
  { key: "inv_total_eligible", label: "Eligible",        color: "#12b5db", w: 0.68 },
  { key: "inv_net_available",  label: "Net available",   color: "#116588", w: 0.38 },
];
const INVMEAS = [
  { key: "gross",      label: "Gross",      header: "inv_gross" },
  { key: "ineligible", label: "Ineligible", header: "inv_total_ineligible" },
  { key: "eligible",   label: "Eligible",   header: "inv_total_eligible" },
  { key: "available",  label: "Available",  header: "inv_available" },
];
const INVCAT = [
  { key: "RM",                 label: "Raw material",   color: "#A5E3F1" },
  { key: "WIP",                label: "WIP",            color: "#7cd4eb" },
  { key: "FG",                 label: "Finished goods", color: "#12b5db" },
  { key: "Packaging/Supplies", label: "Packaging",      color: "#ffb20d" },
  { key: "General",            label: "General",        color: "#e07c1a" },
  { key: "Other",              label: "Other",          color: "#993C1D" },
];

const rowStyle = (r, currentId) => ({ cursor: "pointer", background: r.id === currentId ? B.cyanLight : "" });

// ── A/R Trend: nested A/R bars + Excess / (Deficit) line, with the numbers ──
export const ArTrendCard = ({ hist, currentId, onOpen }) => {
  const [trendOn, setTrendOn] = useState({ end: true, elig: true, net: true, excess: true });
  if (hist.length < 2) return null;
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>A/R Trend {count(hist.length)}</div>
      <div style={{ padding: "8px 12px 4px" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
          {[...TSER, { id: "excess", label: "Excess / (Deficit)", color: "#BA7517", line: true }].map(s => (
            <button key={s.id} onClick={() => setTrendOn(o => ({ ...o, [s.id]: !o[s.id] }))}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", background: "none", cursor: "pointer", fontSize: 11, color: "var(--color-text-secondary)", opacity: trendOn[s.id] ? 1 : 0.4 }}>
              <span style={{ width: s.line ? 14 : 10, height: s.line ? 3 : 10, borderRadius: 2, background: s.color, display: "inline-block" }} />
              {s.label}
            </button>
          ))}
        </div>
        <ArExcessTrend hist={hist} series={TSER.filter(s => trendOn[s.id])} showExcess={trendOn.excess} />
      </div>
      <div style={{ maxHeight: 220, overflowY: "auto", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
          <thead><tr>
            <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Ending A/R</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Eligible A/R</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Net Avail A/R</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Borrowing Base Avail</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Loan Balance</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Excess / (Deficit)</th>
          </tr></thead>
          <tbody>
            {hist.map(r => {
              const ex = parseFloat(r.excess_availability);
              return (
                <tr key={r.id} style={rowStyle(r, currentId)} onClick={() => onOpen(r.id)}>
                  <Td style={{ fontWeight: r.id === currentId ? 600 : 400 }}>{fmtDate(r.asofdate)}</Td>
                  <TdNum>{fmt(r.ar_ending_balance || 0)}</TdNum>
                  <TdNum>{fmt(r.ar_total_eligible || 0)}</TdNum>
                  <TdNum>{fmt(r.ar_net_available || 0)}</TdNum>
                  <TdNum>{fmt(r.borrowing_base_avail || 0)}</TdNum>
                  <TdNum>{fmt(r.current_loan_balance || 0)}</TdNum>
                  <TdNum style={{ color: ex < 0 ? "#993C1D" : "inherit" }}>{fmt(r.excess_availability || 0)}</TdNum>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ── Rollforward A/R Trend: Beginning → activity → Total Ending, with tie-out ──
const parseArRoll = r => { try { return r.ar_rollforward_json ? JSON.parse(r.ar_rollforward_json) : []; } catch (e) { return []; } };
const isArRollPiece = lbl => !/^(beginning a\/r balance|total ending a\/r balance)/i.test(String(lbl || "").trim());

export const ArRollforwardCard = ({ hist, currentId, onOpen }) => {
  if (hist.length < 2) return null;
  const arRollLabels = [];
  hist.forEach(r => parseArRoll(r).forEach(p => { if (isArRollPiece(p.label) && !arRollLabels.includes(p.label)) arRollLabels.push(p.label); }));
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>Rollforward A/R Trend {count(hist.length)}</div>
      <div style={{ padding: "8px 12px 4px" }}>
        <PairTrend hist={hist} series={ARSER} />
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 4 }}>
          {ARSER.map(s => (
            <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--color-text-secondary)" }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, display: "inline-block" }} />{s.label}
            </span>
          ))}
        </div>
      </div>
      <div style={{ maxHeight: 240, overflowY: "auto", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
          <thead><tr>
            <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Beginning A/R</th>
            {arRollLabels.map(lbl => <th key={lbl} style={{ ...thSticky, textAlign: "right" }}>{lbl}</th>)}
            <th style={{ ...thSticky, textAlign: "right" }}>Total Ending A/R</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Prior Ending A/R</th>
            <th style={{ ...thSticky, textAlign: "center" }}>Ties?</th>
          </tr></thead>
          <tbody>
            {hist.map((r, i) => {
              const beg = r.ar_beginning_balance;
              const endAr = r.ar_ending_balance;
              const priorRow = hist[i + 1];
              const priorEndAr = priorRow ? priorRow.ar_ending_balance : null;
              const tie = (beg != null && priorEndAr != null) ? Math.abs(parseFloat(beg) - parseFloat(priorEndAr)) < 0.5 : null;
              const apm = {}; parseArRoll(r).forEach(p => { apm[p.label] = p.value; });
              return (
                <tr key={r.id} style={rowStyle(r, currentId)} onClick={() => onOpen(r.id)}>
                  <Td style={{ fontWeight: r.id === currentId ? 600 : 400 }}>{fmtDate(r.asofdate)}</Td>
                  <TdNum>{beg == null ? "—" : fmt(beg)}</TdNum>
                  {arRollLabels.map(lbl => <TdNum key={lbl} style={{ color: apm[lbl] == null ? "var(--color-text-tertiary)" : "inherit" }}>{apm[lbl] == null ? "—" : fmt(apm[lbl])}</TdNum>)}
                  <TdNum style={{ fontWeight: 600 }}>{endAr == null ? "—" : fmt(endAr)}</TdNum>
                  <TdNum style={{ color: "var(--color-text-secondary)" }}>{priorEndAr == null ? "—" : fmt(priorEndAr)}</TdNum>
                  <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 13 }}>
                    {tie == null ? <span style={{ color: "var(--color-text-tertiary)" }}>—</span>
                      : tie ? <span style={{ color: "#0F6E56", fontWeight: 700 }} title="Beginning A/R ties to prior period's Total Ending A/R">✓</span>
                      : <span style={{ color: "#993C1D", fontWeight: 700 }} title={"Off by " + fmt(Math.abs(parseFloat(beg) - parseFloat(priorEndAr)))}>≠</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ padding: "8px 12px 12px", fontSize: 11, color: "var(--color-text-secondary)" }}>
        Columns between Beginning A/R and Total Ending A/R are the certificate's own A/R rollforward pieces (gross sales, debit/credit memos, collections, discounts, other/timing). <b>Ties?</b>: each period's <b>Beginning A/R Balance</b> should equal the prior certificate's <b>Total Ending A/R Balance</b>.
      </div>
    </Card>
  );
};

// ── Inventory tab ───────────────────────────────────────────────────────────
// invTrend = getInventoryTrend payload ({ header: oldest→newest, categories }).
// cur = the certificate being looked at (its header row; the certificate page
// passes the full header, the dashboard the latest list row). Header figures
// fall back to the invTrend row for the same certificate.
// onSlowMoving: when set, a link to the Slow Moving tab is shown.
export const InventoryTab = ({ invTrend, cur, onOpen, onSlowMoving, hist, skus }) => {
  const [N, setN] = useState(6);   // comparison period for Inventory Performance: 6 (default) or 12 months
  const [invInclExcluded, setInvInclExcluded] = useState(true);
  const [invSerOn, setInvSerOn] = useState({ inv_gross: true, inv_total_eligible: true, inv_net_available: true });
  const [invCatOff, setInvCatOff] = useState({});
  const [invMeas, setInvMeas] = useState("gross");

  const invHist = invTrend.header || [];
  const curId = cur ? cur.id : (invHist.length ? invHist[invHist.length - 1].bbid : null);
  const trendRow = invHist.find(r => r.bbid === curId) || (invHist.length ? invHist[invHist.length - 1] : {});
  const h = { ...trendRow, ...(cur || {}) };
  const pick = k => (cur && cur[k] != null ? cur[k] : trendRow[k]);
  const asof = pick("asofdate");

  const invCatRows = (invTrend.categories || []).filter(c => invInclExcluded || !c.excluded);
  const invCatsPresent = INVCAT.filter(b => invCatRows.some(c => c.category_norm === b.key && (parseFloat(c.gross) || 0) !== 0));
  const invCatAt = (bucket, bbid, meas) => {
    const r = invCatRows.find(c => c.category_norm === bucket && c.bbid === bbid);
    return r ? (parseFloat(r[meas || invMeas]) || 0) : 0;
  };
  const invMeasDef = INVMEAS.find(m => m.key === invMeas) || INVMEAS[0];
  const invCatShown = invCatsPresent.filter(b => !invCatOff[b.key]);
  const invPeriods = invHist.length;
  const invFindings = inventoryInsights(invTrend);
  const curCats = invCatsPresent.filter(b => invCatAt(b.key, curId, "gross") !== 0);

  const gross = pick("inv_gross"), inel = pick("inv_total_ineligible"), elig = pick("inv_total_eligible"), net = pick("inv_net_available");
  const adv = pick("inv_advance_rate"), sub = pick("inv_sublimit"), res = pick("inv_reserve");
  const hasCapFields = h.inv_sublimit !== undefined || h.inv_reserve !== undefined;

  const chip = (on, color) => ({ cursor: "pointer", fontSize: 11, padding: "2px 8px", borderRadius: 999, border: "1px solid " + color,
                                  background: on ? color : "transparent", color: on ? "#fff" : "var(--color-text-secondary)" });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* What inventory is, and what it contributes: gross is what the borrower
          holds, net available is what we lend against. */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {[
          ["Gross at cost", gross, curCats.length ? `${curCats.length} categor${curCats.length === 1 ? "y" : "ies"} reported` : "no category detail"],
          ["Ineligible", inel, gross ? fmtRatio((parseFloat(inel) || 0) / (parseFloat(gross) || 1)) + " of gross" : ""],
          ["Eligible", elig, adv != null ? "advance rate " + fmtRatio(adv) : ""],
          ["Net available", net, hasCapFields
            ? ([sub ? "sublimit " + fmt(sub) : null, res ? "reserve " + fmt(res) : null].filter(Boolean).join(" · ") || "no sublimit or reserve")
            : (asof ? "at " + fmtDate(asof) : "")],
        ].map(([label, value, s]) => (
          <Card key={label} style={{ padding: 16, flex: "1 1 200px", minWidth: 180 }}>
            <div style={cardTitle}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 600 }}>{fmt(value || 0)}</div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4 }}>{s}</div>
          </Card>
        ))}
      </div>

      {/* What changed — one rule set (shared/inventoryInsights.js). Empty when
          nothing crosses a threshold. */}
      {invFindings.length > 0 && (
        <Card style={{ padding: 16 }}>
          <div style={{ ...cardTitle, marginBottom: 6 }}>What changed</div>
          <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
            {invFindings.map(f => (
              <div key={f.key} style={{
                    padding: "8px 10px", borderRadius: 6, fontSize: 12, lineHeight: 1.5,
                    background: f.severity === "watch" ? "#FDF0D5" : "var(--color-background-secondary)",
                    borderLeft: "3px solid " + (f.severity === "watch" ? "#E9A32A" : B.cyan) }}>
                <div style={{ fontWeight: 600, color: f.severity === "watch" ? "#7A5200" : "var(--color-text-primary)" }}>{f.title}</div>
                <div style={{ color: "var(--color-text-secondary)", marginTop: 2 }}>{f.detail}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* appraisal-style monthly view: sales, inventory, YoY, turnover, category change */}
      <InventoryPerformance hist={hist} invTrend={invTrend} N={N} setN={setN} />

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "start" }}>
        {/* composition on THIS certificate */}
        <div style={{ flex: "0 1 380px", minWidth: 280 }}>
          <Card style={{ padding: 16 }}>
            <div style={cardTitle}>Mix at {asof ? fmtDate(asof) : "—"}</div>
            {curCats.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                This certificate reports an inventory total but no category breakdown.
              </div>
            ) : (() => {
              const vals = curCats.map(b => ({ ...b, v: invCatAt(b.key, curId, "gross") }));
              const tot = vals.reduce((a, x) => a + x.v, 0) || 1;
              return <>
                <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", marginBottom: 10 }}>
                  {vals.map(x => <div key={x.key} title={`${x.label}: ${fmt(x.v)}`} style={{ width: (x.v / tot) * 100 + "%", background: x.color }} />)}
                </div>
                {vals.map(x => (
                  <div key={x.key} style={row}>
                    <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: x.color, marginRight: 6 }} />{x.label}</span>
                    <span>{fmt(x.v)} <span style={{ color: "var(--color-text-secondary)" }}>({fmtRatio(x.v / tot)})</span></span>
                  </div>
                ))}
              </>;
            })()}
          </Card>
        </div>

        {/* contribution over time */}
        <div style={{ flex: 1, minWidth: 320 }}>
          <Card style={{ padding: 16 }}>
            <div style={cardTitle}>Inventory Trend</div>
            {invPeriods < 2 ? (
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                One certificate with inventory on file — a trend needs at least two.
              </div>
            ) : <>
              <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 8 }}>
                Each bar sits inside the one before it — eligible is part of gross, net available is part
                of eligible. The gap between the outer and inner bar is what inventory is NOT contributing.
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                {INVSER.map(sx => (
                  <span key={sx.key} onClick={() => setInvSerOn(v => ({ ...v, [sx.key]: !v[sx.key] }))} style={chip(invSerOn[sx.key], sx.color)}>{sx.label}</span>
                ))}
              </div>
              <BarTrend rows={invHist} series={INVSER.filter(sx => invSerOn[sx.key])} scale={0.6} rx={0} />
            </>}
          </Card>
        </div>
      </div>

      {/* the mix over time — stacked, because these categories PARTITION the total */}
      {invCatsPresent.length > 0 && invPeriods >= 2 && (
        <Card style={{ padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ ...cardTitle, marginBottom: 0, borderBottom: "none", paddingBottom: 0 }}>Category Mix Over Time</div>
            <span onClick={() => setInvInclExcluded(v => !v)} style={{ cursor: "pointer", fontSize: 11, color: B.cyanDark, textDecoration: "underline" }}>
              {invInclExcluded ? "Hide excluded categories" : "Include excluded categories"}
            </span>
          </div>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", margin: "4px 0 8px" }}>
            By category, at the measure selected below. Excluded categories are still inventory
            the borrower holds — hiding them shows the pile shrinking when it only stopped counting.
          </div>
          <div style={{ display: "inline-flex", borderRadius: 6, overflow: "hidden", border: "1px solid " + B.cyan, marginBottom: 8 }}>
            {INVMEAS.map(m => (
              <span key={m.key} onClick={() => setInvMeas(m.key)}
                    style={{ cursor: "pointer", fontSize: 11, padding: "3px 12px", fontWeight: invMeas === m.key ? 700 : 400,
                             background: invMeas === m.key ? B.cyan : "transparent", color: invMeas === m.key ? "#fff" : B.cyanDark,
                             borderLeft: m.key === "gross" ? "none" : "1px solid " + B.cyanLight }}>
                {m.label}
              </span>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {invCatsPresent.map(b => (
              <span key={b.key} onClick={() => setInvCatOff(v => ({ ...v, [b.key]: !v[b.key] }))} style={chip(!invCatOff[b.key], b.color)}>{b.label}</span>
            ))}
          </div>
          <StackTrend key={"invcat-" + invMeas} rows={invHist} buckets={invCatShown} value={(r, b) => invCatAt(b.key, r.bbid, invMeas)} />

          <div style={{ maxHeight: 320, overflow: "auto", marginTop: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
              <thead><tr>
                <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
                {invCatsPresent.map(b => <th key={b.key} style={{ ...thSticky, textAlign: "right" }}>{b.label}</th>)}
                {/* The certificate's own figure for the SAME measure, then net
                    available — sublimit and reserve cap inventory as a whole. */}
                <th style={{ ...thSticky, textAlign: "right" }}>Cert {invMeasDef.label.toLowerCase()}</th>
                <th style={{ ...thSticky, textAlign: "right" }}>Net avail</th>
              </tr></thead>
              <tbody>
                {invHist.slice().reverse().map(r => (
                  <tr key={r.bbid} style={{ cursor: "pointer", background: r.bbid === curId ? B.cyanLight : "" }}
                      onClick={() => onOpen(r.bbid)}>
                    <Td style={{ fontWeight: r.bbid === curId ? 600 : 400 }}>{fmtDate(r.asofdate)}</Td>
                    {invCatsPresent.map(b => <TdNum key={b.key}>{fmt(invCatAt(b.key, r.bbid, invMeas))}</TdNum>)}
                    <TdNum>{fmt(r[invMeasDef.header] || 0)}</TdNum>
                    <TdNum>{fmt(r.inv_net_available || 0)}</TdNum>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {onSlowMoving && (
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          Per-SKU ageing and excess-versus-sales analysis is on the{" "}
          <span onClick={onSlowMoving} style={{ color: B.cyanDark, cursor: "pointer", textDecoration: "underline" }}>Slow Moving Inventory</span> tab.
        </div>
      )}
    </div>
  );
};

// ── Debtors table: every debtor, in a box exactly N rows tall ──────────────
// All debtors are rendered (largest open balance first — getBorrowingBase
// orders them); the box is sized (measured, not guessed — row
// heights vary with badges and edit inputs) to show the first N data rows, and
// the rest are reached by scrolling. skip = rows in <tbody> that aren't debtors
// (the dashboard's total row sits in tbody; the certificate page's in thead).
export const DEBTOR_ROWS = 30;
export const RowCapScroll = ({ rows = DEBTOR_ROWS, skip = 0, children }) => {
  const ref = useRef(null);
  const [cap, setCap] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const trs = el.querySelectorAll("tbody tr");
    const idx = rows + skip - 1;
    let next = null;
    if (trs.length > idx + 1) {
      const px = Math.ceil(trs[idx].getBoundingClientRect().bottom - el.getBoundingClientRect().top + el.scrollTop);
      if (px > 0) next = px;
    }
    if (next !== cap) setCap(next);
  });
  return <div ref={ref} style={{ maxHeight: cap || undefined, overflowY: cap ? "auto" : "visible", overflowX: "auto" }}>{children}</div>;
};

// ── A/R Quality: dilution, DSO and collections from the A/R rollforward ─────
// Field-exam measures from data every certificate already carries; the logic
// (and its windows) lives in arMetrics.js. Dilution is the early warning: when
// credits, discounts and write-offs eat a growing share of sales, eligible A/R is
// overstated well before availability moves.
const DIL_WATCH = 0.05, DIL_HIGH = 0.10;           // common ABL thresholds
const dilColor = v => v == null ? "var(--color-text-tertiary)" : v > DIL_HIGH ? "#993C1D" : v > DIL_WATCH ? "#BA7517" : "#12b5db";
const pctTxt = v => v == null ? "—" : (v * 100).toFixed(1) + "%";
const daysTxt = v => v == null ? "—" : Math.round(v) + " days";

export const ArQualityCard = ({ hist, currentId, onOpen }) => {
  if (hist.length < 2) return null;
  const m = arMetricsByRow(hist);
  const anyData = hist.some(r => { const x = m.get(r.id); return x && x.act && x.act.lines > 0; });
  if (!anyData) return null;
  const asc = hist.slice().reverse();
  const cur = hist.find(r => r.id === currentId) || hist[0];
  const cm = m.get(cur.id) || {};
  // the same measure ~30 days earlier, for the arrow on each tile
  const curT = Date.parse(String(cur.asofdate).slice(0, 10));
  const prior = hist.find(r => Date.parse(String(r.asofdate).slice(0, 10)) <= curT - 30 * 86400000);
  const pm = prior ? (m.get(prior.id) || {}) : {};
  const delta = (a, b, fmtD, worseUp) => {
    if (a == null) return <span style={{ color: "var(--color-text-tertiary)" }}>not enough history yet</span>;
    if (b == null) return <span style={{ color: "var(--color-text-tertiary)" }}>no 30-day comparison</span>;
    const d = a - b, worse = worseUp ? d > 0 : d < 0;
    return <span style={{ color: Math.abs(d) < 1e-9 ? "var(--color-text-secondary)" : worse ? "#993C1D" : "#0F6E56" }}>
      {d > 0 ? "▲ " : d < 0 ? "▼ " : ""}{fmtD(Math.abs(d))} vs {fmtDate(prior.asofdate)}</span>;
  };
  const tile = (label, value, sub, color) => (
    <div style={{ flex: "1 1 200px", padding: "10px 12px", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, marginTop: 2, color: color || "inherit" }}>{value}</div>
      <div style={{ fontSize: 11, marginTop: 2 }}>{sub}</div>
    </div>
  );
  const dils = asc.map(r => (m.get(r.id) || {}).dilution);
  const dils30 = asc.map(r => (m.get(r.id) || {}).dilution30);
  const maxDil = Math.max(DIL_HIGH * 1.2, ...dils.filter(v => v != null), ...dils30.filter(v => v != null));
  // Direction: the 30-day rate against the 90-day. A gap of 1 point or more
  // either way is called out — a 30-day rate running above the 90-day one is
  // dilution getting worse right now, before the 90-day number catches up.
  const gap = cm.dilution30 != null && cm.dilution != null ? cm.dilution30 - cm.dilution : null;
  const dir = gap == null ? null : gap >= 0.01 ? "rising" : gap <= -0.01 ? "falling" : "steady";
  const y = stY(0, maxDil), y0 = y(0);

  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ ...cardTitle, padding: "12px 12px 8px", marginBottom: 0 }}>
        A/R Quality <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>· as of {fmtDate(cur.asofdate)}</span>
      </div>
      <div style={{ padding: "8px 12px 4px" }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          {tile("Dilution (30d)", pctTxt(cm.dilution30),
                delta(cm.dilution30, pm.dilution30, v => (v * 100).toFixed(1) + " pts", true), dilColor(cm.dilution30))}
          {tile("Dilution (60d)", pctTxt(cm.dilution60),
                delta(cm.dilution60, pm.dilution60, v => (v * 100).toFixed(1) + " pts", true), dilColor(cm.dilution60))}
          {tile("Dilution (90d)", pctTxt(cm.dilution),
                delta(cm.dilution, pm.dilution, v => (v * 100).toFixed(1) + " pts", true), dilColor(cm.dilution))}
          {tile("DSO (90d)", daysTxt(cm.dso), delta(cm.dso, pm.dso, v => Math.round(v) + " days", true))}
          {tile("Collections (30d)", pctTxt(cm.collectionsPct),
                <>{cm.collectionsPct != null && <span style={{ color: "var(--color-text-secondary)" }}>of A/R at the start of the window · </span>}
                  {delta(cm.collectionsPct, pm.collectionsPct, v => (v * 100).toFixed(1) + " pts", false)}</>)}
        </div>
        {dir && dir !== "steady" && (
          <div style={{ fontSize: 12, padding: "8px 10px", borderRadius: 6, marginBottom: 10, lineHeight: 1.5,
                        background: dir === "rising" ? "#FDF0D5" : "#E1F5EE",
                        borderLeft: "3px solid " + (dir === "rising" ? "#E9A32A" : "#0F6E56"),
                        color: dir === "rising" ? "#7A5200" : "#0F6E56" }}>
            <b>Dilution is {dir}.</b> The last 30 days ran at {pctTxt(cm.dilution30)} against {pctTxt(cm.dilution)} over 90 days
            ({gap > 0 ? "+" : ""}{(gap * 100).toFixed(1)} pts){cm.dilution60 != null ? `, with 60 days at ${pctTxt(cm.dilution60)}` : ""}.
            {dir === "rising" ? " Credit memos and discounts are taking a growing share of sales — eligible A/R may be overstated." : ""}
          </div>
        )}
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 4 }}>
          Dilution by certificate — bars are the 90-day rate (<span style={{ color: "#BA7517" }}>amber</span> above {DIL_WATCH * 100}%,{" "}
          <span style={{ color: "#993C1D" }}>red</span> above {DIL_HIGH * 100}%); the <span style={{ color: "#116588", fontWeight: 600 }}>dark line</span> is
          the 30-day rate. Line above the bars = dilution rising. Dashed line at {DIL_WATCH * 100}%.
        </div>
        <ScrollTrend n={asc.length} labels={asc.map(r => monthLbl(r.asofdate))} ticks={[maxDil, maxDil / 2, 0]} y={y}
          fmtTick={t => (t * 100).toFixed(1) + "%"}
          renderItem={(i, cx, slot) => {
            const v = dils[i]; if (v == null) return null;
            const bw = Math.max(4, slot * 0.6);
            return <rect x={cx - bw / 2} y={y(v)} width={bw} height={Math.max(0, y0 - y(v))} rx="2" fill={dilColor(v)}>
              <title>{`Dilution (90d) · ${fmtDate(asc[i].asofdate)}: ${pctTxt(v)}`}</title>
            </rect>;
          }}
          renderOverlay={(cxOf, slot) => {
            const pts = dils30.map((v, i) => v == null ? null : [cxOf(i), y(v)]);
            const segs = []; let curSeg = [];
            pts.forEach(p => { if (p) curSeg.push(p); else if (curSeg.length) { segs.push(curSeg); curSeg = []; } });
            if (curSeg.length) segs.push(curSeg);
            return <>
              <line x1={0} x2={cxOf(asc.length - 1) + slot} y1={y(DIL_WATCH)} y2={y(DIL_WATCH)} stroke="#BA7517" strokeWidth="1" strokeDasharray="4 3" />
              {segs.map((sg, j) => <polyline key={j} points={sg.map(p => p.join(",")).join(" ")} fill="none" stroke="#116588" strokeWidth="2" />)}
              {dils30.map((v, i) => v == null ? null : <circle key={i} cx={cxOf(i)} cy={y(v)} r="2.6" fill="#116588">
                <title>{`Dilution (30d) · ${fmtDate(asc[i].asofdate)}: ${pctTxt(v)}`}</title></circle>)}
            </>;
          }} />
      </div>
      <div style={{ maxHeight: 240, overflowY: "auto", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
          <thead><tr>
            <th style={{ ...thSticky, textAlign: "left" }}>As-of</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Gross Sales</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Collections</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Credit Memos</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Discounts</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Write-offs</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Dilution (30d)</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Dilution (60d)</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Dilution (90d)</th>
            <th style={{ ...thSticky, textAlign: "right" }}>DSO (90d)</th>
            <th style={{ ...thSticky, textAlign: "right" }}>Collections (30d)</th>
          </tr></thead>
          <tbody>
            {hist.map(r => {
              const x = m.get(r.id) || {}, a = x.act;
              return (
                <tr key={r.id} style={rowStyle(r, currentId)} onClick={() => onOpen(r.id)}>
                  <Td style={{ fontWeight: r.id === currentId ? 600 : 400 }}>{fmtDate(r.asofdate)}</Td>
                  <TdNum>{a ? fmt(a.sales) : "—"}</TdNum>
                  <TdNum>{a ? fmt(a.collections) : "—"}</TdNum>
                  <TdNum>{a ? fmt(a.credits) : "—"}</TdNum>
                  <TdNum>{a ? fmt(a.discounts) : "—"}</TdNum>
                  <TdNum>{a ? fmt(a.writeoffs) : "—"}</TdNum>
                  <TdNum style={{ color: dilColor(x.dilution30) }}>{pctTxt(x.dilution30)}</TdNum>
                  <TdNum style={{ color: dilColor(x.dilution60) }}>{pctTxt(x.dilution60)}</TdNum>
                  <TdNum style={{ color: dilColor(x.dilution), fontWeight: 600 }}>{pctTxt(x.dilution)}</TdNum>
                  <TdNum>{x.dso == null ? "—" : Math.round(x.dso)}</TdNum>
                  <TdNum>{pctTxt(x.collectionsPct)}</TdNum>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ padding: "8px 12px 12px", fontSize: 11, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
        From each certificate's A/R rollforward. <b>Dilution</b> = (credits + discounts + write-offs) ÷ gross sales over the trailing 30, 60 and 90 days — the window's totals, so a big-sales period counts for more than a small one.{" "}
        <b>DSO</b> = ending A/R ÷ average daily gross sales over the same 90 days. <b>Collections</b> = collections over the trailing
        30 days ÷ A/R at the start of those 30 days. Windows are by date, so daily, weekly and monthly reporters compare evenly; early in a
        relationship a window is shown only once there is enough history to fill it (— until then). Amounts are by line label — a line named something unusual is left out. Insights uses this same definition.
      </div>
    </Card>
  );
};

// ── Inventory Performance (appraisal-style, from certificate data) ───────────
// What an inventory appraisal's dashboard shows, rebuilt from what every BBC
// already carries: monthly gross sales (A/R rollforward), month-end inventory
// at cost (inventory header), and inventory by category.
//
// Monthly, because certificates arrive daily, weekly or monthly:
//   sales(month)     = sum of the rollforward periods whose certificate is dated
//                      in that month
//   inventory(month) = gross inventory on the last certificate of that month
// The latest month is "partial" until a certificate dated in a later month
// exists; it is drawn but kept out of every trailing figure.
//
// Comparison period N (6 months by default — most of these loans are short —
// or 12): every "vs" on the card is against N months earlier, and trailing
// sales cover N complete months.
//
// Turnover = trailing-N-month gross sales, annualized (× 12 ÷ N), ÷ month-end inventory. Sales are at
// selling price and inventory at cost, so this reads higher than a COGS-based
// turn. It is consistent month to month, which is what the trend needs.
const ym = d => String(d || "").slice(0, 7);
const ymAdd = (k, n) => { const [y, m] = k.split("-").map(Number); const t = y * 12 + (m - 1) + n; return Math.floor(t / 12) + "-" + String(t % 12 + 1).padStart(2, "0"); };
const ymLbl = k => { const [y, m] = k.split("-"); return ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][+m - 1] + " " + y.slice(2); };
const money = v => v == null ? "—" : fmtShortMoney(v);
const fmtShortMoney = v => { const a = Math.abs(v), s = v < 0 ? "-" : ""; return s + "$" + (a >= 1e6 ? (a / 1e6).toFixed(2) + "M" : a >= 1e3 ? Math.round(a / 1e3) + "K" : Math.round(a)); };
const chgTxt = (a, b) => (a == null || b == null || !b) ? null : (a - b) / Math.abs(b);

const buildMonths = (hist, invTrend, N) => {
  const ascHist = hist.slice().sort((a, b) => String(a.asofdate).localeCompare(String(b.asofdate)));
  const mm = arMetricsByRow(ascHist);
  const sales = {}, inv = {}, invId = {};
  ascHist.forEach(r => { const a = (mm.get(r.id) || {}).act; if (a && a.lines) sales[ym(r.asofdate)] = (sales[ym(r.asofdate)] || 0) + a.sales; });
  (invTrend.header || []).forEach(r => {           // oldest → newest, so the last one in a month wins
    const g = parseFloat(r.inv_gross);
    if (isFinite(g) && g !== 0) { inv[ym(r.asofdate)] = g; invId[ym(r.asofdate)] = r.bbid; }
  });
  const keys = [...new Set([...Object.keys(sales), ...Object.keys(inv)])].sort();
  if (!keys.length) return { months: [] };
  const lastCert = ym(ascHist.length ? ascHist[ascHist.length - 1].asofdate : keys[keys.length - 1]);
  const months = [];
  for (let k = keys[0]; k <= keys[keys.length - 1]; k = ymAdd(k, 1)) {
    months.push({ k, sales: sales[k] != null ? sales[k] : null, inv: inv[k] != null ? inv[k] : null, invId: invId[k] || null, partial: k === lastCert });
  }
  const byK = Object.fromEntries(months.map(m => [m.k, m]));
  // trailing N COMPLETE months of sales ending at month k (null unless all N present)
  const tN = k => {
    let s = 0;
    for (let i = 0; i < N; i++) { const m = byK[ymAdd(k, -i)]; if (!m || m.sales == null || m.partial) return null; s += m.sales; }
    return s;
  };
  // "LY" fields = the same measure N months earlier (a year ago when N = 12)
  months.forEach(m => {
    const ly = byK[ymAdd(m.k, -N)];
    m.salesLY = ly ? ly.sales : null;
    m.invLY = ly ? ly.inv : null;
    m.t12 = m.partial ? null : tN(m.k);
    m.turn = m.t12 != null && m.inv ? (m.t12 * 12 / N) / m.inv : null;
  });
  months.forEach(m => { const ly = byK[ymAdd(m.k, -N)]; m.turnLY = ly ? ly.turn : null; });
  return { months, byK };
};

export const InventoryPerformance = ({ hist, invTrend, N, setN }) => {
  // N (6 or 12) is held by InventoryTab.
  const { months, byK } = buildMonths(hist || [], invTrend || {}, N);
  const ago = N === 12 ? "a year ago" : N + " months ago";
  if (!months.length || !months.some(m => m.inv != null) || !months.some(m => m.sales != null)) return null;
  const complete = months.filter(m => !m.partial);
  const lastC = complete[complete.length - 1];
  const lastInv = [...months].reverse().find(m => m.inv != null);
  const lyInvM = lastInv ? byK[ymAdd(lastInv.k, -N)] : null;
  const s12 = lastC ? lastC.t12 : null;
  const s12p = lastC && byK[ymAdd(lastC.k, -N)] ? byK[ymAdd(lastC.k, -N)].t12 : null;
  const turn = [...complete].reverse().find(m => m.turn != null);
  const hasYoY = complete.some(m => m.salesLY != null && m.sales != null);

  const tile = (label, value, sub, subDelta, goodUp = true) => (
    <div style={{ flex: "1 1 180px", padding: "10px 12px", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: 11, marginTop: 2, color: "var(--color-text-secondary)" }}>
        {sub}{subDelta != null && <span style={{ color: (subDelta >= 0) === goodUp ? "#0F6E56" : "#993C1D", fontWeight: 600 }}>
          {" "}{subDelta >= 0 ? "+" : ""}{(subDelta * 100).toFixed(1)}%</span>}
      </div>
    </div>
  );
  const labels = months.map(m => ymLbl(m.k) + (m.partial ? "*" : ""));

  // Chart 1 — month-end inventory (bars) with monthly sales (line), one $ axis
  const max1 = Math.max(1, ...months.map(m => Math.max(m.inv || 0, m.sales || 0)));
  const y1 = stY(0, max1), y10 = y1(0);
  // Chart 2 — change vs same month last year: sales | inventory
  const ch = months.map(m => ({ s: m.sales != null && m.salesLY != null && !m.partial ? m.sales - m.salesLY : null,
                                i: m.inv != null && m.invLY != null ? m.inv - m.invLY : null }));
  const vals2 = ch.flatMap(c => [c.s, c.i]).filter(v => v != null);
  const max2 = Math.max(1, ...vals2), min2 = Math.min(0, ...vals2);
  const y2 = stY(min2, max2);
  // Chart 3 — turnover this year vs last year
  const tv = months.flatMap(m => [m.turn, m.turnLY]).filter(v => v != null);
  const max3 = Math.max(0.5, ...tv) * 1.1;
  const y3 = stY(0, max3);
  const line = (cxOf, key, color, dash) => {
    const pts = months.map((m, i) => m[key] == null ? null : [cxOf(i), key === "sales" ? y1(m[key]) : y3(m[key])]);
    const segs = []; let cur = [];
    pts.forEach(p => { if (p) cur.push(p); else if (cur.length) { segs.push(cur); cur = []; } });
    if (cur.length) segs.push(cur);
    return segs.map((sg, j) => <polyline key={key + j} points={sg.map(p => p.join(",")).join(" ")} fill="none" stroke={color} strokeWidth="2" strokeDasharray={dash || undefined} />);
  };
  const sub = { fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", margin: "10px 0 2px" };
  const legend = items => (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
      {items.map(([lbl, color, isLine]) => <span key={lbl} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: isLine ? 14 : 10, height: isLine ? 3 : 10, borderRadius: 2, background: color, display: "inline-block" }} />{lbl}</span>)}
    </div>
  );

  // Category change vs ~12 months ago (gross at cost)
  const cats = invTrend.categories || [];
  const latestId = lastInv ? lastInv.invId : null;
  const priorId = lyInvM ? lyInvM.invId : null;
  const catAt = (bbid, key) => cats.filter(c => c.bbid === bbid && c.category_norm === key && !c.excluded)
                                   .reduce((a, c) => a + (parseFloat(c.gross) || 0), 0);
  const catRows = latestId ? INVCAT.map(b => ({ ...b, now: catAt(latestId, b.key), was: priorId ? catAt(priorId, b.key) : null }))
                                   .filter(r => r.now || r.was) : [];
  const catNowTot = catRows.reduce((a, r) => a + r.now, 0) || 1;
  const maxAbs = Math.max(1, ...catRows.map(r => r.was == null ? 0 : Math.abs(r.now - r.was)));

  return (
    <Card style={{ padding: 16 }}>
      <div style={{ ...cardTitle, display: "flex", alignItems: "center", gap: 10 }}>
        <span>Inventory Performance <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>· monthly</span></span>
        <span style={{ marginLeft: "auto", display: "inline-flex", borderRadius: 6, overflow: "hidden", border: "1px solid " + B.cyan, textTransform: "none", letterSpacing: 0 }}>
          {[[6, "vs 6 months ago"], [12, "vs 1 year ago"]].map(([v, l], i) => (
            <span key={v} onClick={() => setN(v)}
                  style={{ cursor: "pointer", fontSize: 11, padding: "3px 12px", fontWeight: N === v ? 700 : 400,
                           background: N === v ? B.cyan : "transparent", color: N === v ? "#fff" : B.cyanDark,
                           borderLeft: i ? "1px solid " + B.cyanLight : "none" }}>{l}</span>
          ))}
        </span>
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        {tile(N + "-Month Gross Sales", money(s12), s12p != null ? "vs prior " + N + " months" : "needs " + (s12 == null ? N : 2 * N) + " complete months", chgTxt(s12, s12p))}
        {tile("Inventory at Cost", money(lastInv && lastInv.inv), lyInvM && lyInvM.inv != null ? "vs " + ymLbl(lyInvM.k) : "no figure from " + ago, chgTxt(lastInv && lastInv.inv, lyInvM && lyInvM.inv), false)}
        {tile("Turnover (annual)", turn ? turn.turn.toFixed(2) + "x" : "—", turn && turn.turnLY != null ? "vs " + turn.turnLY.toFixed(2) + "x " + ago : turn ? "no figure from " + ago : "needs " + N + " complete months", turn && turn.turnLY ? chgTxt(turn.turn, turn.turnLY) : null)}
        {tile("Days on Hand", turn ? Math.round(365 / turn.turn) + " days" : "—", turn && turn.turnLY ? Math.round(365 / turn.turnLY) + " days " + ago : "", null)}
      </div>

      <div style={sub}>Sales and Inventory History</div>
      <ScrollTrend unit="months" n={months.length} labels={labels} ticks={[max1, max1 / 2, 0]} y={y1}
        renderItem={(i, cx, slot) => {
          const m = months[i]; if (m.inv == null) return null; const bw = Math.max(6, slot * 0.6);
          return <rect x={cx - bw / 2} y={y1(m.inv)} width={bw} height={Math.max(0, y10 - y1(m.inv))} rx="2" fill="#A5E3F1">
            <title>{`${ymLbl(m.k)} · inventory at cost: ${fmt(m.inv)}`}</title></rect>;
        }}
        renderOverlay={cxOf => <>
          {line(cxOf, "sales", "#116588")}
          {months.map((m, i) => m.sales == null ? null : <circle key={i} cx={cxOf(i)} cy={y1(m.sales)} r="3" fill={m.partial ? "#fff" : "#116588"} stroke="#116588" strokeWidth="1.5">
            <title>{`${ymLbl(m.k)} · gross sales${m.partial ? " (month in progress)" : ""}: ${fmt(m.sales)}`}</title></circle>)}
        </>} />
      {legend([["Inventory at cost (month-end)", "#A5E3F1"], ["Gross sales (month)", "#116588", true]])}

      {hasYoY && <>
        <div style={sub}>Change vs {N === 12 ? "Same Month Last Year" : N + " Months Earlier"}</div>
        <ScrollTrend unit="months" n={months.length} labels={labels} ticks={min2 < 0 ? [max2, 0, min2] : [max2, max2 / 2, 0]} y={y2} zeroLine={min2 < 0}
          renderItem={(i, cx, slot) => {
            const c = ch[i], bw = Math.max(3, slot * 0.32), out = [];
            [["s", c.s, -1, "Sales"], ["i", c.i, 1, "Inventory"]].forEach(([k, v, side, nm]) => {
              if (v == null) return;
              const x = cx + (side < 0 ? -bw - 1 : 1), top = Math.min(y2(v), y2(0)), hgt = Math.abs(y2(v) - y2(0));
              out.push(<rect key={k} x={x} y={top} width={bw} height={Math.max(1, hgt)} rx="1"
                             fill={k === "s" ? "#116588" : "#12b5db"}>
                <title>{`${ymLbl(months[i].k)} · ${nm} vs ${ymLbl(ymAdd(months[i].k, -N))}: ${v >= 0 ? "+" : ""}${fmt(v)}`}</title></rect>);
            });
            return out;
          }} />
        {legend([["Sales change", "#116588"], ["Inventory change", "#12b5db"]])}
      </>}

      {months.some(m => m.turn != null) && <>
        <div style={sub}>Turnover Trend · Now vs {ago.replace(/^a /, "1 ")}</div>
        <ScrollTrend unit="months" n={months.length} labels={labels} ticks={[max3, max3 / 2, 0]} y={y3} fmtTick={t => t.toFixed(1) + "x"}
          renderItem={() => null}
          renderOverlay={cxOf => <>
            {line(cxOf, "turnLY", "#12b5db", "4 3")}
            {line(cxOf, "turn", "#116588")}
            {months.map((m, i) => m.turn == null ? null : <circle key={i} cx={cxOf(i)} cy={y3(m.turn)} r="3" fill="#116588">
              <title>{`${ymLbl(m.k)} · turnover ${m.turn.toFixed(2)}x${m.turnLY != null ? " (" + ago + " " + m.turnLY.toFixed(2) + "x)" : ""}`}</title></circle>)}
          </>} />
        {legend([["Turnover", "#116588", true], ["Same measure " + ago, "#12b5db", true]])}
      </>}

      {catRows.length > 0 && <>
        <div style={sub}>Category Change {priorId ? `· ${ymLbl(lyInvM.k)} → ${ymLbl(lastInv.k)}` : "· no certificate from " + ago}</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
            <thead><tr>
              <th style={{ ...thSticky, textAlign: "left" }}>Category</th>
              <th style={{ ...thSticky, textAlign: "right" }}>Inventory Now</th>
              <th style={{ ...thSticky, textAlign: "right" }}>% of Total</th>
              <th style={{ ...thSticky, textAlign: "right" }}>{N === 12 ? "A Year Ago" : N + " Months Ago"}</th>
              <th style={{ ...thSticky, textAlign: "right" }}>Change</th>
              <th style={{ ...thSticky, textAlign: "right" }}>Change %</th>
              <th style={{ ...thSticky, textAlign: "left", width: "30%" }}></th>
            </tr></thead>
            <tbody>
              {catRows.sort((a, b) => b.now - a.now).map(r => {
                const d = r.was == null ? null : r.now - r.was;
                return (
                  <tr key={r.key}>
                    <Td><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: r.color, marginRight: 6 }} />{r.label}</Td>
                    <TdNum>{fmt(r.now)}</TdNum>
                    <TdNum>{(r.now / catNowTot * 100).toFixed(1)}%</TdNum>
                    <TdNum>{r.was == null ? "—" : fmt(r.was)}</TdNum>
                    <TdNum style={{ color: d == null ? "inherit" : d < 0 ? "#993C1D" : "#0F6E56" }}>{d == null ? "—" : (d >= 0 ? "+" : "") + fmt(d)}</TdNum>
                    <TdNum>{d == null || !r.was ? "—" : (d >= 0 ? "+" : "") + (d / r.was * 100).toFixed(1) + "%"}</TdNum>
                    <Td>{d != null && <div style={{ height: 8, borderRadius: 4, width: Math.max(2, Math.abs(d) / maxAbs * 100) + "%", background: "#12b5db" }} />}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>}

      <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 10, lineHeight: 1.5 }}>
        Sales are the gross sales on each certificate's A/R rollforward, totalled by month; inventory is gross at cost on the last
        certificate of the month. * marks the month in progress — shown, but left out of every trailing figure. Turnover is
        trailing-{N}-month sales, annualized, ÷ month-end inventory; sales are at selling price and inventory at cost, so it reads higher than a
        COGS-based turn but is consistent over time. Days on hand = 365 ÷ turnover. Margin, units and price-versus-volume need the
        borrower's perpetual and are not on the certificate.
      </div>
    </Card>
  );
};

// ── SKU Analysis (from the slow-moving SKU sheet) ───────────────────────────
// Four appraisal views rebuilt from the per-SKU lines a borrower's slow-moving
// sheet carries (units on hand, cost, start date, units sold in the trailing
// 12 months). Shown only for borrowers who send that sheet, and only on the
// SKUs it lists — which is why its totals can differ from the certificate's
// inventory figure.
//
//   Inventory Demand   each SKU classed by months of supply = units on hand ÷
//                      (TTM units sold ÷ 12): High < 6, Moderate 6–12,
//                      Low > 12 or no sales; New = started in the last 12 months.
//   Change Components  inventory then → cost change → volume change → new SKUs
//                      → removed SKUs → inventory now. For a SKU on both sheets:
//                      cost = (unit cost now − then) × units then;
//                      volume = (units now − then) × unit cost now. Exact: the
//                      two add up to the SKU's change in total cost.
//   Top Movers         the SKUs behind each component.
//   True Turnover      TTM units sold × unit cost = cost of goods sold;
//                      ÷ inventory at cost. Comparable to an appraiser's turn.
// "Then" is the latest SKU sheet dated on or before N months before "now".
const DEMAND = [
  { key: "high", label: "High (< 6 mo supply)",   color: "#116588" },
  { key: "mod",  label: "Moderate (6–12 mo)",     color: "#12b5db" },
  { key: "low",  label: "Low (> 12 mo / no sales)", color: "#BA7517" },
  { key: "new",  label: "New (< 12 mo old)",       color: "#A5E3F1" },
  { key: "unk",  label: "Unclassified (no sales data)", color: "#B4B2A9" },
];
const nOr0 = v => { const x = parseFloat(v); return isFinite(x) ? x : 0; };
const nOrNull = v => { const x = parseFloat(v); return isFinite(x) ? x : null; };
const dayMs = d => Date.parse(String(d || "").slice(0, 10) + "T00:00:00Z");
const ymAddDate = (d, n) => { const t = new Date(dayMs(d)); t.setUTCMonth(t.getUTCMonth() + n); return t.getTime(); };

const classify = (s, asofMs) => {
  const start = s.prod_start_date ? dayMs(s.prod_start_date) : NaN;
  if (isFinite(start) && start > asofMs - 365 * 86400000) return "new";
  const u = nOr0(s.unit_count), sold = nOrNull(s.ttm_units_sold);
  if (sold == null) return "unk";   // sheet gave no TTM units — shown, not hidden
  if (!(sold > 0)) return "low";
  const mos = u / (sold / 12);
  return mos < 6 ? "high" : mos <= 12 ? "mod" : "low";
};

const snapStats = (rows, asof) => {
  const ms = dayMs(asof);
  const dem = { high: 0, mod: 0, low: 0, new: 0, unk: 0 }; let classed = 0, inv = 0, cogs = 0, cogsKnown = false;
  rows.forEach(s => {
    const c = nOr0(s.total_cost); inv += c;
    const k = classify(s, ms);
    dem[k] += c; if (k !== "unk") classed++;
    const sold = nOrNull(s.ttm_units_sold), u = nOr0(s.unit_count);
    if (sold != null && u > 0) { cogs += sold * (c / u); cogsKnown = true; }
  });
  return { dem, classed, inv, cogs: cogsKnown ? cogs : null, turn: cogsKnown && inv > 0 ? cogs / inv : null };
};

const SkuWaterfall = ({ steps }) => {
  // steps: [{label, value, kind: "total"|"delta"}]
  let run = 0; const bars = steps.map(s => {
    if (s.kind === "total") { run = s.value; return { ...s, from: 0, to: s.value }; }
    const from = run; run += s.value; return { ...s, from, to: run };
  });
  const max = Math.max(1, ...bars.map(b => Math.max(b.from, b.to)));
  const H = 200, top = 22, base = H - 38, sc = v => base - (v / max) * (base - top);
  const W = 100 / bars.length;
  return (
    <svg viewBox={`0 0 ${bars.length * 100} ${H}`} width="100%" style={{ display: "block", maxHeight: 240 }}>
      {bars.map((b, i) => {
        const y1 = sc(Math.max(b.from, b.to)), y2 = sc(Math.min(b.from, b.to));
        const color = b.kind === "total" ? "#116588" : b.value >= 0 ? "#12b5db" : "#A5E3F1";
        return <g key={i}>
          <rect x={i * 100 + 22} y={y1} width={56} height={Math.max(1.5, y2 - y1)} fill={color} rx="2">
            <title>{`${b.label}: ${b.kind === "total" ? "" : b.value >= 0 ? "+" : ""}${fmt(b.value)}`}</title></rect>
          <text x={i * 100 + 50} y={y1 - 5} textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--color-text-primary)">
            {(b.kind !== "total" && b.value >= 0 ? "+" : "") + fmtShortMoney(b.value)}</text>
          <text x={i * 100 + 50} y={H - 22} textAnchor="middle" fontSize="10.5" fill="var(--color-text-secondary)">{b.label.split("\n")[0]}</text>
          <text x={i * 100 + 50} y={H - 9} textAnchor="middle" fontSize="10.5" fill="var(--color-text-secondary)">{b.label.split("\n")[1] || ""}</text>
        </g>;
      })}
    </svg>
  );
};

export const SkuAnalysis = ({ skus, curId, curAsof }) => {
  const [mover, setMover] = useState("volume");
  const [N, setN] = useState(6);   // compare against 6 (default) or 12 months ago
  const bySnap = useMemo(() => {
    const m = new Map();
    (skus || []).forEach(s => { if (!m.has(s.bbid)) m.set(s.bbid, { bbid: s.bbid, asofdate: s.asofdate, rows: [] }); m.get(s.bbid).rows.push(s); });
    return [...m.values()].sort((a, b) => String(a.asofdate).localeCompare(String(b.asofdate)));
  }, [skus]);
  const stats = useMemo(() => bySnap.map(sn => ({ ...sn, ...snapStats(sn.rows, sn.asofdate) })), [bySnap]);
  if (!bySnap.length) return null;

  // "now" = the certificate in view if it has a SKU sheet, else the latest sheet on or before it
  const curMs = curAsof ? dayMs(curAsof) : Infinity;
  const nowSnap = stats.find(s => s.bbid === curId) || [...stats].reverse().find(s => dayMs(s.asofdate) <= curMs) || stats[stats.length - 1];
  const target = ymAddDate(nowSnap.asofdate, -N);
  const thenSnap = [...stats].reverse().find(s => dayMs(s.asofdate) <= target) || null;
  const ago = N === 12 ? "a year ago" : N + " months ago";

  // ── change components ──
  let comp = null, movers = null;
  if (thenSnap) {
    const P = new Map(thenSnap.rows.map(r => [r.unique_id, r])), C = new Map(nowSnap.rows.map(r => [r.unique_id, r]));
    const cost = [], vol = [], nw = [], rm = [];
    C.forEach((c, id) => {
      const p = P.get(id), tcC = nOr0(c.total_cost), uC = nOr0(c.unit_count);
      if (!p) { if (tcC) nw.push({ id, d: c.description, uP: null, uC, tcP: 0, tcC, v: tcC }); return; }
      const tcP = nOr0(p.total_cost), uP = nOr0(p.unit_count);
      if (uC > 0 && uP > 0) {
        const ucC = tcC / uC, ucP = tcP / uP;
        const ce = (ucC - ucP) * uP, ve = (uC - uP) * ucC;
        if (Math.abs(ce) >= 0.5) cost.push({ id, d: c.description, uP, uC, ucP, ucC, tcP, tcC, v: ce });
        if (Math.abs(ve) >= 0.5) vol.push({ id, d: c.description, uP, uC, ucP, ucC, tcP, tcC, v: ve });
      } else if (Math.abs(tcC - tcP) >= 0.5) vol.push({ id, d: c.description, uP, uC, tcP, tcC, v: tcC - tcP });
    });
    P.forEach((p, id) => { if (!C.has(id) && nOr0(p.total_cost)) rm.push({ id, d: p.description, uP: nOr0(p.unit_count), uC: null, tcP: nOr0(p.total_cost), tcC: 0, v: -nOr0(p.total_cost) }); });
    const sum = a => a.reduce((t, x) => t + x.v, 0);
    comp = { then: thenSnap.inv, cost: sum(cost), vol: sum(vol), nw: sum(nw), rm: sum(rm), now: nowSnap.inv };
    movers = { cost, volume: vol, new: nw, removed: rm };
  }

  // Shares are of ALL inventory on the sheet; SKUs with no sales data sit in
  // their own grey Unclassified bucket rather than dropping out of the base.
  const demTot = Object.values(nowSnap.dem).reduce((a, b) => a + b, 0);
  const hasDemand = nowSnap.classed > 0;
  const demTrend = stats.filter(s => s.classed > 0);
  const turnTrend = stats.filter(s => s.turn != null);
  const sub = { fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", margin: "12px 0 4px" };
  const pctOf = (v, t) => t ? (v / t * 100).toFixed(1) + "%" : "—";
  const tile = (label, value, s2, color) => (
    <div style={{ flex: "1 1 180px", padding: "10px 12px", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, marginTop: 2, color: color || "inherit" }}>{value}</div>
      <div style={{ fontSize: 11, marginTop: 2, color: "var(--color-text-secondary)" }}>{s2}</div>
    </div>
  );
  const mv = movers ? movers[mover].slice().sort((a, b) => Math.abs(b.v) - Math.abs(a.v)).slice(0, 15) : [];
  const turnMax = Math.max(0.5, ...turnTrend.map(s => s.turn)) * 1.1;
  const yT = stY(0, turnMax);

  return (
    <Card style={{ padding: 16 }}>
      <div style={{ ...cardTitle, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span>SKU Analysis <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>
          · sheet at {fmtDate(nowSnap.asofdate)} · {nowSnap.rows.length.toLocaleString()} SKUs{thenSnap ? ` · vs ${fmtDate(thenSnap.asofdate)}` : ""}</span></span>
        <span style={{ marginLeft: "auto", display: "inline-flex", borderRadius: 6, overflow: "hidden", border: "1px solid " + B.cyan, textTransform: "none", letterSpacing: 0 }}>
          {[[6, "vs 6 months ago"], [12, "vs 1 year ago"]].map(([v, l], i) => (
            <span key={v} onClick={() => setN(v)}
                  style={{ cursor: "pointer", fontSize: 11, padding: "3px 12px", fontWeight: N === v ? 700 : 400,
                           background: N === v ? B.cyan : "transparent", color: N === v ? "#fff" : B.cyanDark,
                           borderLeft: i ? "1px solid " + B.cyanLight : "none" }}>{l}</span>
          ))}
        </span>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {tile("Inventory on Sheet", fmtShortMoney(nowSnap.inv), comp ? `${comp.now >= comp.then ? "+" : ""}${fmtShortMoney(comp.now - comp.then)} vs ${ago}` : "no sheet from " + ago)}
        {tile("Turnover (COGS)", nowSnap.turn != null ? nowSnap.turn.toFixed(2) + "x" : "—",
              nowSnap.turn == null ? "sheet has no TTM units sold" : thenSnap && thenSnap.turn != null ? `vs ${thenSnap.turn.toFixed(2)}x ${ago}` : "TTM units sold × unit cost ÷ inventory")}
        {tile("Days on Hand", nowSnap.turn ? Math.round(365 / nowSnap.turn) + " days" : "—", thenSnap && thenSnap.turn ? Math.round(365 / thenSnap.turn) + " days " + ago : "")}
        {tile("Low Demand", hasDemand ? pctOf(nowSnap.dem.low, demTot) : "—", hasDemand ? fmtShortMoney(nowSnap.dem.low) + " over 12 months' supply" + (nowSnap.dem.unk > 0 ? " · " + pctOf(nowSnap.dem.unk, demTot) + " unclassified" : "") : "",
              hasDemand && nowSnap.dem.low / (demTot || 1) > 0.25 ? "#993C1D" : undefined)}
      </div>

      {hasDemand && <>
        <div style={sub}>Inventory Demand · {fmtDate(nowSnap.asofdate)}</div>
        <div style={{ display: "flex", height: 14, borderRadius: 7, overflow: "hidden", marginBottom: 8 }}>
          {DEMAND.map(b => nowSnap.dem[b.key] > 0 && <div key={b.key} title={`${b.label}: ${fmt(nowSnap.dem[b.key])}`} style={{ width: (nowSnap.dem[b.key] / demTot * 100) + "%", background: b.color }} />)}
        </div>
        {/* One row per class — label, dollars, a bar of its share, percent — so
            each figure sits right beside the class it belongs to. */}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(170px, max-content) minmax(110px, max-content) minmax(120px, 360px) 52px",
                      columnGap: 12, rowGap: 6, alignItems: "center", fontSize: 12, maxWidth: 760 }}>
          {DEMAND.map(b => {
            const v = nowSnap.dem[b.key], share = demTot ? v / demTot : 0;
            return [
              <span key={b.key + "l"}><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: b.color, marginRight: 6 }} />{b.label}</span>,
              <span key={b.key + "v"} style={{ textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmt(v)}</span>,
              <div key={b.key + "b"} style={{ height: 10, borderRadius: 3, background: "var(--color-background-secondary)" }}>
                <div style={{ width: Math.max(share > 0 ? 1 : 0, share * 100) + "%", height: "100%", borderRadius: 3, background: b.color }} />
              </div>,
              <span key={b.key + "p"} style={{ textAlign: "right", color: "var(--color-text-secondary)", fontVariantNumeric: "tabular-nums" }}>{pctOf(v, demTot)}</span>,
            ];
          })}
        </div>
        {demTrend.length > 1 && <>
          <div style={sub}>Demand Over Time</div>
          <StackTrend rows={demTrend} buckets={DEMAND} value={(r, b) => r.dem[b.key]} />
        </>}
      </>}
      {!hasDemand && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 8 }}>
        This borrower's sheet does not report units sold, so SKUs cannot be classed by demand.</div>}

      <div style={sub}>Inventory Change Components {thenSnap ? `· ${fmtDate(thenSnap.asofdate)} → ${fmtDate(nowSnap.asofdate)}` : ""}</div>
      {!comp
        ? <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>No SKU sheet from {ago} or earlier to compare against.</div>
        : <>
          <SkuWaterfall steps={[
            { label: "Inventory\n" + fmtDate(thenSnap.asofdate), value: comp.then, kind: "total" },
            { label: "Change due to\ncost", value: comp.cost },
            { label: "Change due to\nvolume", value: comp.vol },
            { label: "New\nSKUs", value: comp.nw },
            { label: "Removed\nSKUs", value: comp.rm },
            { label: "Inventory\n" + fmtDate(nowSnap.asofdate), value: comp.now, kind: "total" },
          ]} />
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", margin: "8px 0 6px" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)" }}>Top movers</span>
            <span style={{ display: "inline-flex", borderRadius: 6, overflow: "hidden", border: "1px solid " + B.cyan }}>
              {[["volume", "Volume"], ["cost", "Cost"], ["new", "New SKUs"], ["removed", "Removed SKUs"]].map(([k, l], i) => (
                <span key={k} onClick={() => setMover(k)} style={{ cursor: "pointer", fontSize: 11, padding: "3px 12px", fontWeight: mover === k ? 700 : 400,
                  background: mover === k ? B.cyan : "transparent", color: mover === k ? "#fff" : B.cyanDark, borderLeft: i ? "1px solid " + B.cyanLight : "none" }}>
                  {l} ({movers[k].length})</span>
              ))}
            </span>
          </div>
          <div style={{ maxHeight: 360, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
              <thead><tr>
                <th style={{ ...thSticky, textAlign: "left" }}>SKU</th>
                <th style={{ ...thSticky, textAlign: "left", width: "auto" }}>Description</th>
                <th style={{ ...thSticky, textAlign: "right" }}>Units Then</th>
                <th style={{ ...thSticky, textAlign: "right" }}>Units Now</th>
                <th style={{ ...thSticky, textAlign: "right" }}>Unit Cost Then</th>
                <th style={{ ...thSticky, textAlign: "right" }}>Unit Cost Now</th>
                <th style={{ ...thSticky, textAlign: "right" }}>Cost Then</th>
                <th style={{ ...thSticky, textAlign: "right" }}>Cost Now</th>
                <th style={{ ...thSticky, textAlign: "right" }}>{mover === "cost" ? "Due to Cost" : mover === "volume" ? "Due to Volume" : "Change"}</th>
              </tr></thead>
              <tbody>
                {mv.length === 0
                  ? <tr><td colSpan={9} style={{ padding: 14, textAlign: "center", fontSize: 12, color: "var(--color-text-secondary)" }}>None.</td></tr>
                  : mv.map(x => (
                    <tr key={x.id}>
                      <Td style={{ fontWeight: 500 }}>{x.id}</Td>
                      <Td style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }} title={x.d || ""}>{x.d || "—"}</Td>
                      <TdNum>{x.uP == null ? "—" : Math.round(x.uP).toLocaleString()}</TdNum>
                      <TdNum>{x.uC == null ? "—" : Math.round(x.uC).toLocaleString()}</TdNum>
                      <TdNum>{x.ucP == null ? "—" : fmt(x.ucP)}</TdNum>
                      <TdNum>{x.ucC == null ? "—" : fmt(x.ucC)}</TdNum>
                      <TdNum>{fmt(x.tcP)}</TdNum>
                      <TdNum>{fmt(x.tcC)}</TdNum>
                      <TdNum style={{ fontWeight: 600, color: x.v < 0 ? "#993C1D" : "#0F6E56" }}>{(x.v >= 0 ? "+" : "") + fmt(x.v)}</TdNum>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {movers[mover].length > 15 && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>Largest 15 of {movers[mover].length} by size of change.</div>}
        </>}

      {turnTrend.length > 1 && <>
        <div style={sub}>Turnover (COGS) Over Time</div>
        <ScrollTrend n={turnTrend.length} labels={turnTrend.map(s => monthLbl(s.asofdate))} ticks={[turnMax, turnMax / 2, 0]} y={yT}
          fmtTick={t => t.toFixed(1) + "x"}
          renderItem={(i, cx, slot) => { const v = turnTrend[i].turn, bw = Math.max(5, slot * 0.5);
            return <rect x={cx - bw / 2} y={yT(v)} width={bw} height={Math.max(0, yT(0) - yT(v))} rx="2" fill="#116588">
              <title>{`${fmtDate(turnTrend[i].asofdate)} · turnover ${v.toFixed(2)}x (${Math.round(365 / v)} days)`}</title></rect>; }} />
      </>}

      <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 10, lineHeight: 1.5 }}>
        From the borrower's slow-moving SKU sheet, every SKU it lists (included or not), so totals can differ from the certificate's inventory.
        <b> Demand</b>: months of supply = units on hand ÷ (TTM units sold ÷ 12); New = production start within 12 months.
        <b> Components</b>: for a SKU on both sheets, cost change = (unit cost now − then) × units then and volume change = (units now − then) × unit cost now;
        SKUs on only one sheet are new or removed. <b>Turnover</b> = TTM units sold × current unit cost (cost of goods sold) ÷ inventory at cost —
        comparable to an appraiser's turn. "Then" is the latest sheet dated on or before {ago}.
      </div>
    </Card>
  );
};
